// import { DoubleArrowDownIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";

import { useDateRangeStore } from "./_components/date-range-picker/use-date-range-store";
// import { useStatusFilter } from "./hooks/use-status-filter";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";

import { fetchAllLeadsQueryOptions } from "@/config/queries/emp-query-options";
import { convertUTCtoIST } from "@/utils";
import { Lead } from "./_components/leads-table/schema";
import { getColumns } from "./_components/leads-table/columns";
import { DataTable } from "./_components/leads-table/data-table";

import { DataTable as MyAssignedDataTable } from "./_components/my-assigned-leads-table/data-table";
import { getColumns as getMyAssignedColumns } from "./_components/my-assigned-leads-table/columns";

import { DateRangePicker } from "./_components/date-range-picker/date-range-picker2";
import { useAuth } from "@/lib/use-auth";
import { MyLead } from "./_components/my-assigned-leads-table/schema";
import { TakeActionOnLeadModal } from "./_components/take-action-modal/take-action-modal";

import { searchableColumns as MyAssignedSearchableColumns } from "./_components/my-assigned-leads-table/columns";
import { searchableColumns as AvailableSearchableColumns } from "./_components/leads-table/columns";
import { CreateLead } from "./_components/create-lead";

const LeadsForEmployees = () => {
  const key = Cookies.get("key") as string;
  const { data: authData } = useAuth();

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const { data, isPending, isFetching } = useQuery(
    fetchAllLeadsQueryOptions(key, {
      startdate: convertUTCtoIST(new Date(startdate.setHours(0))),
      enddate: convertUTCtoIST(new Date(enddate.setHours(23))),
    })
  );

  const availableLeads = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.leads === undefined) {
      return [];
    }

    const result = data?.leads?.map((lead) => {
      return {
        id: lead?._id,
        name: lead?.name,
        status: lead?.status,
        phone: lead?.phone,
        alloted: lead?.alloted,
        broker: lead?.broker,
        originalStatus: lead?.originalStatus,
      };
    });

    const filtered = result?.filter((lead) => !lead.alloted);

    return filtered;
  }, [data, isPending, isFetching]);

  const myAssignedLeads = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.leads === undefined) {
      return [];
    }

    const result = data?.leads?.map((lead) => {
      return {
        id: lead?._id,
        mname: lead?.name,
        status: lead?.status,
        phone: lead?.phone,
        alloted: lead?.alloted,
        allotedTo: lead?.allotedTo?._id,
        allotedAt: lead?.allotedAt as string | Date,
        broker: lead?.broker,
        originalStatus: lead?.originalStatus,
      };
    });

    const filtered = result?.filter(
      (lead) => lead?.allotedTo === (authData?.user?._id as string)
    );

    return filtered;
  }, [data, isPending, isFetching]);

  const availableColumns = useMemo<ColumnDef<Lead, unknown>[]>(
    () => getColumns(),
    []
  );

  const assignedColumns = useMemo<ColumnDef<MyLead, unknown>[]>(
    () => getMyAssignedColumns(),
    []
  );

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Leads</h3>

        <div className="flex items-center gap-2">
          <DateRangePicker />
        </div>
      </div>

      <div className="mt-3">
        <Tabs defaultValue="my-assigned-leads" className="w-full">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="my-assigned-leads">
                My Assigned Leads
              </TabsTrigger>
              <TabsTrigger value="available-leads">Available Leads</TabsTrigger>
            </TabsList>
            <CreateLead />
          </div>
          <TabsContent value="my-assigned-leads">
            <div className="mt-5 mb-10 pb-10">
              <MyAssignedDataTable
                data={myAssignedLeads}
                columns={assignedColumns}
                searchableColumns={MyAssignedSearchableColumns}
              />
            </div>
          </TabsContent>
          <TabsContent value="available-leads">
            <div className="mt-5 mb-10 pb-10">
              <DataTable
                data={availableLeads}
                columns={availableColumns}
                searchableColumns={AvailableSearchableColumns}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TakeActionOnLeadModal />
    </div>
  );
};

export default LeadsForEmployees;
